import moment from "moment-timezone";


export function getRating(perf) {
    return `${perf?.rating ?? "-"}${perf?.prov ? "?" : ""}`;
}
export const userRoles = ["user", "moderator", "admin", "owner"];

export function roleToLabel(role) {
    const roleLabels = {
        guest: "Гость",
        user: "Пользователь",
        moderator: "Модератор",
        admin: "Администратор",
        owner: "Владелец",
    };
    return roleLabels[ role ] || "Неизвестная роль";
}

export function roleToSymbol(role) {
    const roleSymbols = {
        guest: "○",
        user: "♟",
        moderator: "♞",
        admin: "♛",
        owner: "♚",
    };
    return roleSymbols[ role ] || "?";
}
/**
 *
 * @param {Object} user
 * @param {userRoles} role
 * @returns {Boolean} allowed
 */
export function checkRole(user, role) {
    if (!user) return false;
    const userRoleIndex = userRoles.indexOf(user.role);
    const roleIndex = userRoles.indexOf(role);
    if (roleIndex === -1) return false;
    return userRoleIndex >= roleIndex;
}

// Стадии обучающей программы. Значения ездят в URL (/program/<level>/<stage>/),
// поэтому список один на все экраны.
export const PROGRAM_STAGES = [
    { value: "opening", label: "Дебют" },
    { value: "middlegame", label: "Миттельшпиль" },
    { value: "endgame", label: "Эндшпиль" },
];

export const DEFAULT_PROGRAM_STAGE = "middlegame";

/**
 * Resolve a level id (e.g. "queen" or "queen+") to its progression rank.
 * The `levels` array is ordered ascending by rating, so the index is the rank.
 * The "+" (Супер) suffix is a within-tier qualifier, not a separate rung, so
 * it is stripped before lookup. Returns -1 when the level can't be resolved.
 */
export const getLevelRank = (level, levels) => {
    if (!level || !Array.isArray(levels)) return -1;
    const baseLevel = level.replace("+", "");
    return levels.findIndex((lvl) => lvl.id === baseLevel);
};

/**
 * Whether a user is above a challenge's maximum allowed level. A challenge
 * with no level ("Любой") has no restriction. Fails open (returns false) when
 * either level can't be resolved, so the backend stays the real gate.
 * @param {string} userLevel
 * @param {string} challengeLevel
 * @param {Array} levels
 * @returns {Boolean}
 */
export const isLevelAboveChallenge = (userLevel, challengeLevel, levels) => {
    if (!challengeLevel) return false;
    const userRank = getLevelRank(userLevel, levels);
    const challengeRank = getLevelRank(challengeLevel, levels);
    if (userRank === -1 || challengeRank === -1) return false;
    return userRank > challengeRank;
};

/**
 * Display name for a level id, e.g. "queen+" -> " Суперферзь".
 * @param {String|null|undefined} level
 * @param {Array} levels
 * @returns {String} rank name, or "Звание не присвоено" when unresolvable
 */
export const getDisplayRankName = (level, levels) => {
    const NO_RANK = "Звание не присвоено";
    if (!level || typeof level !== "string") return NO_RANK;
    const isSuper = level.includes("+");
    const baseLevel = isSuper ? level.replace("+", "") : level;
    const rankObj = Array.isArray(levels) ? levels.find((lvl) => lvl.id === baseLevel) : null;
    if (!rankObj) return NO_RANK;
    return isSuper ? ` Супер${rankObj.name}` : rankObj.name;
};

export const pluralRu = (n, [one, few, many]) => {
    const abs = Math.abs(n) % 100;
    const last = abs % 10;
    if (abs > 10 && abs < 20) return many;
    if (last === 1) return one;
    if (last >= 2 && last <= 4) return few;
    return many;
};

/**
 * Relative time in Russian, e.g. "3 недели назад" (moment ru locale is not bundled)
 * @param {String|Number|Date} date
 * @returns {String} human-readable time distance or "-" when date is missing/invalid
 */
export function timeAgoRu(date) {
    if (!date) return "-";
    const timestamp = new Date(date).getTime();
    if (Number.isNaN(timestamp)) return "-";

    const diffMinutes = Math.floor((Date.now() - timestamp) / 60000);
    if (diffMinutes < 1) return "только что";

    const units = [
        { limit: 60, divisor: 1, forms: ["минуту", "минуты", "минут"] },
        { limit: 60 * 24, divisor: 60, forms: ["час", "часа", "часов"] },
        { limit: 60 * 24 * 7, divisor: 60 * 24, forms: ["день", "дня", "дней"] },
        { limit: 60 * 24 * 30, divisor: 60 * 24 * 7, forms: ["неделю", "недели", "недель"] },
        { limit: 60 * 24 * 365, divisor: 60 * 24 * 30, forms: ["месяц", "месяца", "месяцев"] },
        { limit: Infinity, divisor: 60 * 24 * 365, forms: ["год", "года", "лет"] },
    ];
    const unit = units.find(u => diffMinutes < u.limit);
    const value = Math.max(1, Math.floor(diffMinutes / unit.divisor));
    return `${value} ${pluralRu(value, unit.forms)} назад`;
}

/**
 * Format a signed lichess rating change, e.g. 45 -> "+45", -12 -> "−12", 0/null -> "-"
 * @param {Number|null|undefined} delta
 * @returns {String}
 */
export function formatRatingDelta(delta) {
    if (delta === null || delta === undefined || Number.isNaN(delta)) return "-";
    if (delta > 0) return `+${delta}`;
    if (delta < 0) return `−${Math.abs(delta)}`;
    return "0";
}

/**
 * CSS class for a signed lichess rating change, matching formatRatingDelta's sign.
 * @param {Number|null|undefined} delta
 * @returns {String}
 */
export function ratingDeltaClass(delta) {
    if (delta === null || delta === undefined || Number.isNaN(delta)) return "sg-delta-none";
    if (delta > 0) return "sg-delta-pos";
    if (delta < 0) return "sg-delta-neg";
    return "sg-delta-zero";
}

/**
 * Compact win/loss(/draw) summary for a lichess puzzles or games record,
 * e.g. { won: 22, lost: 8 } -> "22W / 8L", { won: 7, lost: 4, draws: 1 } -> "7W / 4L / 1D"
 * @param {{won: Number, lost: Number, draws?: Number}|null|undefined} record
 * @returns {String}
 */
export function formatWinLoss(record) {
    if (!record) return "-";
    const parts = [`${record.won ?? 0}W`, `${record.lost ?? 0}L`];
    if (record.draws !== undefined && record.draws !== null) parts.push(`${record.draws}D`);
    return parts.join(" / ");
}

/**
 * Check if user subscription is active
 * @param {Object} user
 * @returns {Boolean} true if subscription is active
 */
export function isSubscriptionActive(user) {
    if (!user?.subscription) return false;
    
    const subscriptionValue = user.subscription;
    let timestamp;
    
    // Handle different timestamp formats
    if (typeof subscriptionValue === 'string') {
        timestamp = new Date(subscriptionValue).getTime();
    } else if (typeof subscriptionValue === 'number') {
        // Convert seconds to milliseconds if needed (timestamp < 10000000000 is likely in seconds)
        timestamp = subscriptionValue > 10000000000 ? subscriptionValue : subscriptionValue * 1000;
    } else {
        return false;
    }
    
    return timestamp > Date.now();
}

// Plain loop instead of `value.replace(/\/+$/, "")`: without a leading `^`
// anchor, that regex retries the backtracking search from every start
// position after each failed match, making it O(n^2) on a long run of
// slashes not at the very end of the string. This stays O(n).
export const stripTrailingSlashes = (value) => {
    let result = value;
    while (result.endsWith("/")) {
        result = result.slice(0, -1);
    }
    return result;
};

/**
 * Today as "YYYY-MM-DD" (UTC, consistent with toISOString usage elsewhere)
 */
export const todayIso = () => new Date().toISOString().split("T")[0];

/**
 * Normalize any date value (full ISO timestamp, Date, "YYYY-MM-DD") to "YYYY-MM-DD".
 * Returns null when the value can't be parsed.
 */
export const toIsoDay = (value) => {
    if (!value) return null;
    const s = String(value);
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? null : d.toISOString().split("T")[0];
};

/**
 * Add delta days to a "YYYY-MM-DD" date, returning "YYYY-MM-DD"
 */
export const addIsoDays = (iso, delta) => {
    const d = new Date(`${iso}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + delta);
    return d.toISOString().split("T")[0];
};

/**
 * Calendar day "YYYY-MM-DD" in the viewer's own date format (from the browser locale).
 * A calendar day has no time zone, so it is formatted as UTC to keep the same day.
 */
export const formatIsoDate = (iso) => new Intl.DateTimeFormat(undefined, { dateStyle: "short", timeZone: "UTC" })
    .format(new Date(`${iso}T00:00:00Z`));

const toValidDate = (value) => {
    if (value === null || value === undefined || value === "") return null;
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
};

const DATE_TIME_SHORT = { dateStyle: "short", timeStyle: "short" };

/**
 * A moment in time (ISO string, millis, Date or moment) in the viewer's own
 * locale and time zone, both taken from the browser. "-" when missing or invalid.
 * @param {Intl.DateTimeFormatOptions} [options] defaults to short date + short time
 */
export const formatDateTime = (value, options = DATE_TIME_SHORT) => {
    const date = toValidDate(value);
    return date ? new Intl.DateTimeFormat(undefined, options).format(date) : "-";
};

/** Date part only of a moment in time, in the viewer's locale and time zone. */
export const formatDate = (value) => formatDateTime(value, { dateStyle: "short" });

/** A moment in time as the value of an <input type="datetime-local"> (viewer's time zone). */
export const toDatetimeLocal = (value) => {
    const date = toValidDate(value);
    if (!date) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
        + `T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

/** The value of an <input type="datetime-local"> (viewer's time zone) as a UTC ISO string, or null. */
export const fromDatetimeLocal = (value) => toValidDate(value)?.toISOString() ?? null;

/**
 * Tooltip text for a daily-goals day entry (same wording as the profile heatmap)
 */
export const dailyStatsTitle = (iso, stats) => {
    if (stats && Object.hasOwn(stats, "completed")) {
        const games = `${stats.games ?? 0}/${stats.gamesNeeded ?? "?"} игр`;
        const puzzles = `${stats.puzzles ?? 0}/${stats.puzzlesNeeded ?? "?"} задач`;
        const streak = `серия ${stats.streak || 0}`;
        return `${formatIsoDate(iso)}: ${games}, ${puzzles}, ${streak}`;
    }
    return `${formatIsoDate(iso)}: нет данных`;
};

/**
 * Parse a subscription end value (ISO string or unix seconds/millis) into
 * a moment, or null when absent/invalid
 */
export const parseSubscriptionEnd = (value) => {
    if (!value) return null;
    if (typeof value === "number") {
        const timestamp = value > 10000000000 ? value : value * 1000;
        const parsed = moment(timestamp);
        return parsed.isValid() ? parsed : null;
    }
    const parsed = moment(value);
    return parsed.isValid() ? parsed : null;
};

const firstWord = (text) => String(text ?? "").trim().split(/\s+/)[0].toLowerCase();

// Same word up to the ending: "пешки" ~ "пешка", "кони" ~ "конь".
const sameWord = (a, b) => {
    if (!a || !b) return false;
    let common = 0;
    while (common < a.length && common < b.length && a[common] === b[common]) common++;
    return common >= Math.max(3, Math.min(a.length, b.length) - 1);
};

// Rating range { min, max } (max null = no cap) of the level whose first name word
// matches the first word of `name`, case-insensitive and ignoring the ending.
// null when no level matches.
export const levelRangeByName = (name, levels) => {
    const word = firstWord(name);
    const sorted = [...(levels ?? [])].sort((a, b) => a.rating - b.rating);
    const index = sorted.findIndex(level => sameWord(firstWord(level.name), word));
    if (index < 0) return null;
    const next = sorted[index + 1];
    return { min: sorted[index].rating, max: next ? next.rating - 1 : null };
};
